import { useBatches, useRecipes } from '@ctcells/api'
import type { Batch, Recipe } from '@ctcells/contract'
import { dataSourceFor } from '@ctcells/core'
import { ACTIVE_BATCHES, RECIPES } from '@ctcells/fixtures'

/**
 * **「지금 도는 작업」이 화면으로 들어오는 단 하나의 문.**
 *
 * 세 자리가 같은 물음을 한다 — 상시 작업 줄(`BatchStripContainer`) · 모니터링 따라가기
 * (`useRunningDeviceKey`) · 시료 흐름(`useSampleFlow`). 셋이 **같은 배치를 말해야** 한 화면이
 * 두 말을 하지 않는다. 그래서 질의 인자(한도 · `phase`)도, 목이 서는 자리도 여기 한 곳이다.
 *
 * 🔴 **`demo` 면 fixture 가 «서버 자리»에 선다 — 그 «위»는 갈리지 않는다.**
 *    2026-09-21 사고가 이것 때문이었다: demo 에서 세 자리가 전부 `skip` 만 하고 대타를 두지
 *    않아 **작업이 `undefined`** 였고, 화면은 「원심 실행 중」이라 말하면서 같은 화면에서
 *    「진행 중인 작업 없음」이라고 말했다. 모듈·시료는 fixture 로 찼는데 **작업만 비어서**
 *    시료 흐름(`samplesOfBatch`)이 한 건도 못 골랐다.
 *    ⇒ 갈리는 것은 **값의 출처 하나**다. 고르기·이름 붙이기·거르기는 목이든 실물이든 같은 코드다.
 *
 * 🔴 **도메인마다 제 스위치로 가른다**(`batches` · `recipes`). 한쪽 스위치로 둘 다 끄면
 *    「레시피는 실물인데 작업만 목」 같은 조합에서 **실물을 때리지 않는다**(또는 그 반대로 샌다).
 *    ⚠ 섞인 조합에서는 목 작업의 `recipe_id` 가 실물 목록에 없어 이름이 안 붙는다 — 그때 줄은
 *      `recipe_id` 원값을 적는다(`stripRecipeNames`). 지어내지 않는 쪽이 맞다.
 *
 * 🔴 **주기 폴링이 없다**(DP-01). 스냅샷 1회 + 구독 무효화다.
 *
 * ## 🔴 끝난 작업은 «보이지 않는다» (eric 확정 2026-09-22 「작업이 끝나면 기존 배치랑 레시피는 안 보이게 해야지」)
 *
 * 서버의 `phase=active` 가 종착(`completed`·`aborted`)을 빼므로 **그 목록을 그대로 보이면 된다.** 배치가 끝나는
 * 순간 작업 줄은 「작업 선택」 대기로, 시료 판은 점선 자리 카드(환자 4 · 대조 1)로 돌아간다.
 * ⚠ 같은 날 오전에 팀장이 「보고 있던 작업은 끝나도 남긴다」(종착 한 건을 `GET /batches/{id}` 로 받아 붙들기)로
 *   갔었는데 eric 이 뒤집었다. 그 길(`watchedBatchId` · `useBatch` 추적 조회)은 걷었다 — 되살리지 마라.
 *   대가는 하나다: 환자 카드 단계 줄의 다섯째 칸 「완료」는 live 에서 잠깐도 안 보인다(차는 순간 목록에서 빠진다).
 *   그 사실은 작업 이력(`/batches`)과 보고서가 말한다.
 */

/** 진행 중 작업을 한 번에 받는 상한. 서버 최대가 500 이라 그 아래로 둔다 */
export const ACTIVE_BATCH_LIMIT = 200

export interface ActiveBatchSource {
  /** `GET /batches?phase=active` 의 `items` — 서버 차례 그대로. 못 봤으면 빈 배열. 종착은 서버가 뺀다 */
  batches: readonly Batch[]
  /** `GET /recipes` 의 `items`. 작업에 이름을 붙이고 묶음 구성을 읽는 데 쓴다 */
  recipes: readonly Recipe[]
}

export function useActiveBatchSource(): ActiveBatchSource {
  const liveRecipes = dataSourceFor('recipes') === 'live'

  /* 🔴 훅은 조건 없이 부른다 — 끄는 것은 `skip` 이다(훅 차례가 렌더마다 같아야 한다) */
  const batches = useActiveBatches()
  const recipes = useRecipes({ limit: ACTIVE_BATCH_LIMIT, skip: !liveRecipes })

  return {
    batches,
    recipes: liveRecipes ? (recipes.data?.items ?? []) : RECIPES,
  }
}

/**
 * 같은 문의 **작업 절반만.** 레시피 이름이 필요 없는 자리(「대기」 사유의 「다른 작업」 대조)가 쓴다 —
 * 레시피 목록까지 부르면 그 자리에 없던 `GET /recipes` 가 생긴다. 질의 인자는 위와 **같다**(같은 캐시).
 */
export function useActiveBatches(): readonly Batch[] {
  const liveBatches = dataSourceFor('batches') === 'live'
  const batches = useBatches({ limit: ACTIVE_BATCH_LIMIT, phase: 'active', skip: !liveBatches })
  return liveBatches ? (batches.data?.items ?? []) : ACTIVE_BATCHES
}
